'use client';

import React from 'react';
import { Button as AntButton, ConfigProvider } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';
import { orionTheme } from '../theme/orion-theme';
import clsx from 'clsx';

export interface ButtonProps extends AntButtonProps {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * Button component - Ant Design Button with OpenStore Orion theming
 * 
 * This component provides FULL access to all Ant Design Button props while
 * automatically applying the OpenStore Orion theme.
 * 
 * Features:
 * - Figma design specifications (#1D4A88 primary color)
 * - Automatic Orion theming
 * - Tailwind class override support
 * - ALL Ant Design Button features and props
 * 
 * @example
 * ```tsx
 * // All Ant Design Button props are available
 * <Button type="primary">Add Product</Button>
 * <Button type="default">Cancel</Button>
 * <Button type="dashed">Dashed</Button>
 * <Button type="text">Text Button</Button>
 * <Button type="link">Link Button</Button>
 * 
 * // All Ant Design features work
 * <Button type="primary" icon={<PlusOutlined />}>With Icon</Button>
 * <Button type="primary" loading>Loading</Button>
 * <Button type="primary" danger>Danger</Button>
 * <Button type="primary" disabled>Disabled</Button>
 * <Button type="primary" block>Full Width</Button>
 * <Button type="primary" size="large">Large</Button>
 * <Button type="primary" shape="round">Round</Button>
 * <Button type="primary" ghost>Ghost</Button>
 * 
 * // With Tailwind overrides
 * <Button 
 *   type="primary" 
 *   className="shadow-lg transform hover:scale-105"
 * >
 *   Custom Styled
 * </Button>
 * 
 * // All event handlers work
 * <Button 
 *   type="primary"
 *   onClick={handleClick}
 *   onMouseEnter={handleHover}
 *   onFocus={handleFocus}
 * >
 *   Interactive Button
 * </Button>
 * ```
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      ...props // All other Ant Design props pass through
    },
    ref
  ) => {
    return (
      <ConfigProvider theme={orionTheme}>
        <AntButton
          ref={ref}
          className={clsx(
            'orion-button',
            // Add custom classes for Tailwind overrides
            className
          )}
          {...props} // Pass through ALL Ant Design props
        >
          {children}
        </AntButton>
      </ConfigProvider>
    );
  }
);

Button.displayName = 'Button';

// Export convenience components for common patterns (optional)
export const PrimaryButton: React.FC<Omit<ButtonProps, 'type'>> = (props) => (
  <Button type="primary" {...props} />
);

export const SecondaryButton: React.FC<Omit<ButtonProps, 'type'>> = (props) => (
  <Button type="default" {...props} />
);

export const DangerButton: React.FC<Omit<ButtonProps, 'type' | 'danger'>> = (props) => (
  <Button type="primary" danger {...props} />
);

export const GhostButton: React.FC<Omit<ButtonProps, 'type'>> = (props) => (
  <Button type="text" {...props} />
);